<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import RealEstateCommissionSupportFilters from '../components/RealEstateCommissionSupportFilters.vue';
import RealEstateCommissionSupportHeader from '../components/RealEstateCommissionSupportHeader.vue';
import RealEstateCommissionSupportTable from '../components/RealEstateCommissionSupportTable.vue';
import RealEstateCommissionSupportWelcome from '../components/RealEstateCommissionSupportWelcome.vue';
import { useRealEstateCommissionSupport } from '../composables/useRealEstateCommissionSupport';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const support = useRealEstateCommissionSupport({ notify: useAlert, t });
const advancedFiltersOpen = ref(false);
const orderOpen = ref(false);

const tabs = computed(() => [
  {
    id: 'para_enviar',
    label: t('REAL_ESTATE_COMMISSION_SUPPORT.TABS.PENDING'),
  },
  {
    id: 'enviadas',
    label: t('REAL_ESTATE_COMMISSION_SUPPORT.TABS.SENT'),
  },
]);

const firstName = computed(
  () => currentUser.value?.name?.split(' ')?.[0] || ''
);
const refreshLabel = computed(() =>
  support.minutesSinceRefresh.value === null
    ? ''
    : t('REAL_ESTATE_COMMISSION_SUPPORT.ACTIONS.REFRESHED_AT', {
        minutes: support.minutesSinceRefresh.value,
      })
);

const openPlan = row =>
  router.push({
    name: 'operacao_imobiliaria_apoio_planilha_comissao',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: row.processo_id,
    },
  });

const openDetails = row =>
  router.push({
    name: 'operacao_imobiliaria_apoio_detalhes',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: row.processo_id,
    },
  });

const toggleAdvancedFilters = () => {
  advancedFiltersOpen.value = !advancedFiltersOpen.value;
  if (advancedFiltersOpen.value) orderOpen.value = false;
};

const toggleOrder = () => {
  orderOpen.value = !orderOpen.value;
  if (orderOpen.value) advancedFiltersOpen.value = false;
};
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <RealEstateCommissionSupportHeader
      v-model="support.tab.value"
      :tabs="tabs"
      :is-loading="support.loading.value"
      :refresh-label="refreshLabel"
      @update:model-value="support.setTab"
      @refresh="support.load"
    />
    <main
      class="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden px-[21px] pb-8 pt-4"
    >
      <RealEstateCommissionSupportWelcome
        v-if="support.noticeVisible.value"
        :first-name="firstName"
        :new-processes="support.totalRows.value"
        @dismiss="support.dismissNotice"
      />
      <RealEstateCommissionSupportFilters
        :filters="support.filters.value"
        :catalog="support.catalog.value"
        :loading="support.loading.value || support.loadingCatalog.value"
        :show-advanced="advancedFiltersOpen"
        :show-order="orderOpen"
        @apply="support.applyFilters"
        @clear="support.clearFilters"
      />
      <p
        v-if="support.error.value"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ support.error.value }}
      </p>
      <RealEstateCommissionSupportTable
        :rows="support.rows.value"
        :loading="support.loading.value"
        :tab="support.tab.value"
        :page="support.page.value"
        :total-rows="support.totalRows.value"
        :total-pages="support.totalPages.value"
        @open="openPlan"
        @details="openDetails"
        @change-page="support.changePage"
        @toggle-filters="toggleAdvancedFilters"
        @toggle-order="toggleOrder"
      />
    </main>
  </div>
</template>
